import React from "react";
import { CCol, CFormGroup, CLabel, CRow } from "@coreui/react";
import StampSelect from "../../stamps/StampSelect";
import EstablishmentSelect from "../../establishments/EstablishmentSelect";
import DispatchPointSelect from "../../dispatch-point/DispatchPointSelect";
import Datetime from "react-datetime";
import { useAutoInvoiceFormContext } from "../context/AutoInvoiceFormContext";
import { FieldErrors } from "../../form/FieldErrors";
import { getFieldErrors } from "../../../models/errors";

const SectionDivider: React.FC<{ icon: string; label: string }> = ({ icon, label }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: "16px", marginTop: "16px" }}>
    <div style={{ width: "4px", height: "18px", background: "#006657", borderRadius: "2px", marginRight: "10px", flexShrink: 0 }} />
    <i className={`fa ${icon} mr-2`} style={{ fontSize: "0.85rem", color: "var(--theme-accent)" }}></i>
    <h6 style={{ margin: 0, fontWeight: 600, color: "var(--theme-muted)", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.8px" }}>
      {label}
    </h6>
    <div style={{ flex: 1, height: "1px", background: "var(--theme-divider)", marginLeft: "10px" }} />
  </div>
);

const FormLabel: React.FC<{ required?: boolean; children: React.ReactNode }> = ({ required, children }) => (
  <CLabel className="mb-0" style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--theme-text-soft)" }}>
    {required && <span className="text-danger mr-1">*</span>}
    {children}
  </CLabel>
);

const AutoInvoiceFormHeader: React.FC = () => {
  const {
    stamp,
    establishment,
    dispatchPoint,
    autoInvoiceDate,
    errors,
    onStampChange,
    onEstablishmentChange,
    onDispatchPointChange,
    onAutoInvoiceDateChange,
  } = useAutoInvoiceFormContext();

  const handleEstablishmentChange = (est: typeof establishment) => {
    onEstablishmentChange(est);
    onDispatchPointChange(null);
  };

  return (
    <>
      <SectionDivider icon="fa-info-circle" label="Datos Generales" />
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Timbrado:</FormLabel>
          </CCol>
          <CCol>
            <StampSelect onChange={onStampChange} value={stamp} />
            <FieldErrors
              errors={getFieldErrors("stampId", errors) as string[]}
            />
            <FieldErrors
              errors={getFieldErrors("stampIdentifier", errors) as string[]}
            />
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Establecimiento:</FormLabel>
          </CCol>
          <CCol>
            <EstablishmentSelect
              onChange={handleEstablishmentChange}
              value={establishment}
            />
            <FieldErrors
              errors={getFieldErrors("establishmentId", errors) as string[]}
            />
            <FieldErrors
              errors={getFieldErrors("establishmentCode", errors) as string[]}
            />
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Punto de Expedición:</FormLabel>
          </CCol>
          <CCol>
            <DispatchPointSelect
              key={establishment?.id ?? 0}
              onChange={onDispatchPointChange}
              value={dispatchPoint}
              establishmentId={establishment?.id}
            />
            <FieldErrors
              errors={getFieldErrors("dispatchPointId", errors) as string[]}
            />
            <FieldErrors
              errors={
                getFieldErrors("dispatchPointIdentifier", errors) as string[]
              }
            />
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Fecha de emisión:</FormLabel>
          </CCol>
          <CCol>
            <Datetime
              className="pl-0"
              onChange={onAutoInvoiceDateChange}
              value={autoInvoiceDate}
              locale="es/PY"
              dateFormat="DD/MM/YYYY"
              timeFormat="HH:mm"
              closeOnSelect={true}
            />
            <FieldErrors
              errors={getFieldErrors("autoInvoiceDate", errors) as string[]}
            />
          </CCol>
        </CRow>
      </CFormGroup>
    </>
  );
};

export default AutoInvoiceFormHeader;
